import React, { useState, useMemo, useEffect } from "react";

/* ===================================================================
   RIZEE HR MANAGEMENT — shared theme tokens, sample data & UI primitives
   Uses inline styles + FontAwesome (fas fa-*) icons + recharts, matching
   the rest of the Rizee college front-end (no Tailwind / lucide).
=================================================================== */

export const T = {
  ink: "#232A4D",
  paper: "#F3F4F7",
  panel: "#FFFFFF",
  gold: "#C1872E",
  goldSoft: "#F3E3C8",
  teal: "#2F7A5F",
  tealSoft: "#DDEFE7",
  coral: "#C1483E",
  coralSoft: "#F6DEDC",
  amber: "#B8860B",
  amberSoft: "#F3E7C9",
  info: "#3D6FB0",
  infoSoft: "#E6EFFA",
  slate: "#6B7280",
  rule: "#DCDFE6",
};

/* ─── Departments & Designations ─────────────────────────────────── */
export const DEPARTMENTS = [
  { id: "D1", name: "Physics", code: "PHY", head: "Anitha Kumari", staff: 14 },
  { id: "D2", name: "Chemistry", code: "CHM", head: "Lakshmi Priya", staff: 12 },
  { id: "D3", name: "Mathematics", code: "MTH", head: "Anitha Kumari", staff: 11 },
  { id: "D4", name: "Biology", code: "BIO", head: "Divya Prasanna", staff: 9 },
  { id: "D5", name: "Administration", code: "ADM", head: "Ramesh Babu", staff: 8 },
  { id: "D6", name: "Examinations", code: "EXM", head: "Farooq Ahmed", staff: 6 },
  { id: "D7", name: "IT & Digital", code: "ITS", head: "Vijay Kumar", staff: 5 },
  { id: "D8", name: "Accounts", code: "ACC", head: "Sowmya Latha", staff: 7 },
];

export const DESIGNATIONS = [
  { id: "T1", title: "Principal", dept: "Administration", level: 1, reportsTo: "—" },
  { id: "T2", title: "HOD", dept: "Physics", level: 2, reportsTo: "Principal" },
  { id: "T3", title: "Senior Faculty", dept: "Physics", level: 3, reportsTo: "HOD" },
  { id: "T4", title: "Faculty", dept: "Chemistry", level: 4, reportsTo: "Senior Faculty" },
  { id: "T5", title: "Academic Coordinator", dept: "Administration", level: 3, reportsTo: "Principal" },
  { id: "T6", title: "Exam Coordinator", dept: "Examinations", level: 3, reportsTo: "Principal" },
  { id: "T7", title: "System Administrator", dept: "IT & Digital", level: 4, reportsTo: "Principal" },
  { id: "T8", title: "Accountant", dept: "Accounts", level: 4, reportsTo: "Principal" },
  { id: "T9", title: "Front Office Executive", dept: "Administration", level: 5, reportsTo: "Academic Coordinator" },
  { id: "T10", title: "Lab Assistant", dept: "Biology", level: 5, reportsTo: "Faculty" },
];

/* ─── Employees ──────────────────────────────────────────────────── */
export const EMPLOYEES = [
  { id: "RZ-T-0142", name: "Kavitha Reddy", type: "Teaching", dept: "Physics", designation: "Senior Faculty", status: "Active", joinDate: "2021-06-14", phone: "+91 98481 22310", email: "kavitha.reddy@rizee.in", qualification: "M.Sc Physics, B.Ed", exp: 9, gender: "Female", salary: 62000, attendanceToday: "Present", address: "12, Jubilee Hills, Hyderabad" },
  { id: "RZ-T-0198", name: "Suresh Chandra Rao", type: "Teaching", dept: "Chemistry", designation: "Faculty", status: "Active", joinDate: "2022-01-10", phone: "+91 90000 11223", email: "suresh.rao@rizee.in", qualification: "M.Sc Chemistry", exp: 5, gender: "Male", salary: 48000, attendanceToday: "Present", address: "45, Kukatpally, Hyderabad" },
  { id: "RZ-T-0211", name: "Anitha Kumari", type: "Teaching", dept: "Mathematics", designation: "HOD", status: "Active", joinDate: "2018-04-02", phone: "+91 98765 44100", email: "anitha.k@rizee.in", qualification: "M.Sc Maths, Ph.D", exp: 12, gender: "Female", salary: 84000, attendanceToday: "Present", address: "8, Madhapur, Hyderabad" },
  { id: "RZ-N-0056", name: "Ramesh Babu", type: "Non-Teaching", dept: "Administration", designation: "Admin Officer", status: "On Leave", joinDate: "2019-09-23", phone: "+91 91234 56789", email: "ramesh.babu@rizee.in", qualification: "MBA", exp: 7, gender: "Male", salary: 55000, attendanceToday: "On Leave", address: "23, Ameerpet, Hyderabad" },
  { id: "RZ-T-0233", name: "Divya Prasanna", type: "Teaching", dept: "Biology", designation: "Faculty", status: "Probation", joinDate: "2026-04-01", phone: "+91 99887 76655", email: "divya.p@rizee.in", qualification: "M.Sc Zoology", exp: 2, gender: "Female", salary: 41000, attendanceToday: "Late", address: "9, Miyapur, Hyderabad" },
  { id: "RZ-N-0071", name: "Vijay Kumar", type: "Non-Teaching", dept: "IT & Digital", designation: "System Administrator", status: "Active", joinDate: "2020-11-11", phone: "+91 90111 22344", email: "vijay.kumar@rizee.in", qualification: "B.Tech CSE", exp: 6, gender: "Male", salary: 58000, attendanceToday: "Present", address: "77, Banjara Hills, Hyderabad" },
  { id: "RZ-T-0180", name: "Lakshmi Priya", type: "Teaching", dept: "Chemistry", designation: "Senior Faculty", status: "Active", joinDate: "2017-07-19", phone: "+91 98230 45566", email: "lakshmi.priya@rizee.in", qualification: "M.Sc Chemistry, B.Ed", exp: 13, gender: "Female", salary: 66000, attendanceToday: "Present", address: "34, Kondapur, Hyderabad" },
  { id: "RZ-N-0088", name: "Farooq Ahmed", type: "Non-Teaching", dept: "Examinations", designation: "Exam Coordinator", status: "Active", joinDate: "2021-02-08", phone: "+91 93456 78901", email: "farooq.a@rizee.in", qualification: "M.Com", exp: 8, gender: "Male", salary: 52000, attendanceToday: "Present", address: "56, Gachibowli, Hyderabad" },
  { id: "RZ-T-0245", name: "Naveen Chowdary", type: "Teaching", dept: "Physics", designation: "Faculty", status: "Resigned", joinDate: "2019-03-15", phone: "+91 91122 33445", email: "naveen.c@rizee.in", qualification: "M.Sc Physics", exp: 6, gender: "Male", salary: 47000, attendanceToday: "On Leave", address: "18, Nizampet, Hyderabad" },
  { id: "RZ-N-0102", name: "Sowmya Latha", type: "Non-Teaching", dept: "Accounts", designation: "Front Office Executive", status: "Active", joinDate: "2023-08-01", phone: "+91 90909 80808", email: "sowmya.l@rizee.in", qualification: "B.Com", exp: 3, gender: "Female", salary: 38000, attendanceToday: "Present", address: "61, KPHB, Hyderabad" },
  { id: "RZ-T-0260", name: "Prashanth Reddy", type: "Teaching", dept: "Mathematics", designation: "Faculty", status: "Active", joinDate: "2022-06-20", phone: "+91 99009 11223", email: "prashanth.r@rizee.in", qualification: "M.Sc Maths", exp: 4, gender: "Male", salary: 45000, attendanceToday: "Present", address: "29, Manikonda, Hyderabad" },
  { id: "RZ-N-0119", name: "Deepak Sharma", type: "Non-Teaching", dept: "IT & Digital", designation: "Lab Assistant", status: "Active", joinDate: "2024-01-05", phone: "+91 98123 45677", email: "deepak.s@rizee.in", qualification: "B.Sc Computers", exp: 2, gender: "Male", salary: 33000, attendanceToday: "Late", address: "14, Chandanagar, Hyderabad" },
  { id: "RZ-T-0275", name: "Meena Krishnan", type: "Teaching", dept: "Biology", designation: "Senior Faculty", status: "Active", joinDate: "2016-05-30", phone: "+91 98888 12345", email: "meena.k@rizee.in", qualification: "M.Sc Botany, Ph.D", exp: 14, gender: "Female", salary: 71000, attendanceToday: "Present", address: "3, Tolichowki, Hyderabad" },
];

export const SERVICE_HISTORY = {
  "RZ-T-0142": [
    { date: "2021-06-14", event: "Joined as Faculty — Physics" },
    { date: "2023-04-01", event: "Promoted to Senior Faculty" },
    { date: "2025-01-10", event: "Appraisal — Rated Excellent" },
  ],
  "RZ-T-0211": [
    { date: "2018-04-02", event: "Joined as Faculty — Mathematics" },
    { date: "2020-05-12", event: "Promoted to Senior Faculty" },
    { date: "2023-06-01", event: "Appointed HOD — Mathematics" },
  ],
};

/* ─── Recruitment ────────────────────────────────────────────────── */
export const OPENINGS = [
  { id: "JR-01", role: "Physics Faculty", dept: "Physics", type: "Full-time", applicants: 18, stage: "Interview", status: "Open" },
  { id: "JR-02", role: "Lab Assistant", dept: "Biology", type: "Full-time", applicants: 24, stage: "Screening", status: "Open" },
  { id: "JR-03", role: "Front Office Executive", dept: "Administration", type: "Full-time", applicants: 11, stage: "Offer", status: "Open" },
];
export const CANDIDATES = [
  { name: "Ravi Teja", role: "Physics Faculty", stage: "Interview", score: 82, applied: "2026-06-28" },
  { name: "Sneha Menon", role: "Physics Faculty", stage: "Screening", score: 74, applied: "2026-06-30" },
  { name: "Karthik Nair", role: "Lab Assistant", stage: "Offer", score: 88, applied: "2026-06-22" },
  { name: "Pooja Rani", role: "Front Office Executive", stage: "Interview", score: 79, applied: "2026-07-01" },
  { name: "Anjali Varma", role: "Lab Assistant", stage: "Screening", score: 68, applied: "2026-07-02" },
];
export const ONBOARDING = [
  { name: "Divya Prasanna", task: "Document verification", status: "Pending", due: "2026-07-10" },
  { name: "Divya Prasanna", task: "ID card issuance", status: "Pending", due: "2026-07-12" },
  { name: "Deepak Sharma", task: "Asset allocation", status: "Completed", due: "2026-01-06" },
  { name: "Sowmya Latha", task: "Bank details", status: "Completed", due: "2023-08-03" },
];

/* ─── Attendance ─────────────────────────────────────────────────── */
export const ATTENDANCE_MONTH = Array.from({ length: 30 }).map((_, i) => ({
  day: i + 1,
  present: 22 + ((i * 7) % 6),
  absent: (i * 3) % 3,
  late: (i * 5) % 2,
}));

/* ─── Leave ──────────────────────────────────────────────────────── */
export const LEAVE_TYPES = [
  { type: "Casual Leave", used: 6, total: 12 },
  { type: "Sick Leave", used: 3, total: 10 },
  { type: "Earned Leave", used: 8, total: 15 },
  { type: "Maternity / Paternity", used: 0, total: 90 },
];
export const LEAVE_REQUESTS = EMPLOYEES.slice(0, 10).map((e, i) => ({
  id: `LR${2000 + i}`,
  employee: e.name,
  dept: e.dept,
  type: ["Casual Leave", "Sick Leave", "Earned Leave"][i % 3],
  from: `0${(i % 9) + 1}-07-2026`,
  to: `0${(i % 9) + 2}-07-2026`,
  days: 1 + (i % 3),
  reason: ["Family function", "Fever, rest advised", "Personal work", "Travel", "Medical checkup"][i % 5],
  status: i < 4 ? "Pending" : i < 8 ? "Approved" : "Rejected",
}));
export const HOLIDAYS = [
  { date: "15-Aug-2026", name: "Independence Day" },
  { date: "02-Oct-2026", name: "Gandhi Jayanti" },
  { date: "20-Oct-2026", name: "Vijayadashami" },
  { date: "08-Nov-2026", name: "Diwali" },
  { date: "25-Dec-2026", name: "Christmas" },
];

/* ─── Payroll ────────────────────────────────────────────────────── */
export const PAYSLIPS = EMPLOYEES.filter((e) => e.status !== "Resigned").slice(0, 8).map((e, i) => ({
  id: `PS${3000 + i}`,
  employee: e.name,
  month: "June 2026",
  gross: e.salary,
  deductions: Math.round(e.salary * 0.12),
  net: Math.round(e.salary * 0.88),
  status: i < 6 ? "Paid" : "Processing",
}));
export const SALARY_HISTORY = [
  { month: "Feb", net: 512000 }, { month: "Mar", net: 528000 }, { month: "Apr", net: 519000 },
  { month: "May", net: 541000 }, { month: "Jun", net: 555000 }, { month: "Jul", net: 562000 },
];

/* ─── Performance ────────────────────────────────────────────────── */
export const APPRAISALS = EMPLOYEES.filter((e) => e.status === "Active").slice(0, 8).map((e, i) => ({
  employee: e.name,
  dept: e.dept,
  cycle: "Apr–Sep 2026",
  kpi: 62 + ((i * 13) % 36),
  rating: (3 + ((i * 7) % 20) / 10).toFixed(1),
  status: i < 5 ? "Completed" : "In Progress",
}));
export const PROMOTIONS = [
  { employee: "Kavitha Reddy", from: "Faculty", to: "Senior Faculty", date: "Apr 2023" },
  { employee: "Anitha Kumari", from: "Senior Faculty", to: "HOD", date: "Jun 2023" },
  { employee: "Deepak Sharma", from: "Trainee", to: "Lab Assistant", date: "Jan 2024" },
];

/* ─── Documents ──────────────────────────────────────────────────── */
export const DOCUMENTS = [
  { name: "Aadhaar Card", employee: "Kavitha Reddy", type: "Identity", status: "Verified" },
  { name: "PAN Card", employee: "Kavitha Reddy", type: "Identity", status: "Verified" },
  { name: "Degree Certificate", employee: "Suresh Chandra Rao", type: "Qualification", status: "Pending" },
  { name: "Experience Letter", employee: "Lakshmi Priya", type: "Employment", status: "Verified" },
  { name: "Relieving Letter", employee: "Naveen Chowdary", type: "Employment", status: "Rejected" },
  { name: "Bank Passbook", employee: "Vijay Kumar", type: "Financial", status: "Verified" },
  { name: "Appointment Letter", employee: "Divya Prasanna", type: "Employment", status: "Pending" },
];

/* ─── Exit management ────────────────────────────────────────────── */
export const EXITS = [
  { employee: "Naveen Chowdary", dept: "Physics", type: "Resignation", lastDay: "2026-07-31", clearance: "In Progress" },
  { employee: "Ravi Shankar", dept: "Accounts", type: "Retirement", lastDay: "2026-08-31", clearance: "Pending" },
];
export const CLEARANCE_ITEMS = [
  { item: "Asset return (laptop, ID card)", owner: "IT & Digital", status: "Pending" },
  { item: "Library dues", owner: "Library", status: "Cleared" },
  { item: "Accounts settlement", owner: "Accounts", status: "In Progress" },
  { item: "Exit interview", owner: "HR", status: "Pending" },
  { item: "Knowledge transfer", owner: "HOD - Physics", status: "Cleared" },
];

/* ─── Lecturer time table & workload ─────────────────────────────── */
export const TT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const TT_PERIODS = [
  { id: "P1", label: "Period 1", time: "09:00 – 09:50" },
  { id: "P2", label: "Period 2", time: "09:50 – 10:40" },
  { id: "P3", label: "Period 3", time: "11:00 – 11:50" },
  { id: "P4", label: "Period 4", time: "11:50 – 12:40" },
  { id: "P5", label: "Period 5", time: "01:30 – 02:20" },
  { id: "P6", label: "Period 6", time: "02:20 – 03:10" },
];

// Each cell = { subject, cls, room } or null (free period). Keyed by employee id.
const _slot = (subject, cls, room) => ({ subject, cls, room });
export const TIMETABLE = {
  "RZ-T-0142": {
    Mon: [_slot("Physics", "Sr MPC-A", "301"), _slot("Physics", "Sr MPC-B", "302"), null, _slot("Physics Lab", "Jr MPC-A", "Lab-1"), _slot("Physics", "Jr MPC-A", "205"), null],
    Tue: [_slot("Physics", "Sr MPC-A", "301"), null, _slot("Physics", "Jr MPC-B", "206"), _slot("Physics", "Sr MPC-B", "302"), null, _slot("Mentoring", "Sr MPC-A", "301")],
    Wed: [_slot("Physics", "Jr MPC-A", "205"), _slot("Physics", "Jr MPC-A", "205"), _slot("Physics Lab", "Sr MPC-A", "Lab-1"), null, _slot("Physics", "Sr MPC-B", "302"), null],
    Thu: [_slot("Physics", "Sr MPC-A", "301"), _slot("Physics", "Sr MPC-B", "302"), null, _slot("Physics", "Jr MPC-B", "206"), _slot("Physics", "Jr MPC-A", "205"), null],
    Fri: [null, _slot("Physics", "Sr MPC-A", "301"), _slot("Physics", "Jr MPC-A", "205"), _slot("Physics Lab", "Jr MPC-B", "Lab-1"), null, _slot("Doubt Clearing", "Sr MPC", "301")],
    Sat: [_slot("Physics", "Sr MPC-B", "302"), _slot("Physics", "Jr MPC-A", "205"), null, _slot("Weekly Test", "Sr MPC", "Hall-1"), null, null],
  },
  "RZ-T-0198": {
    Mon: [_slot("Chemistry", "Jr BiPC-A", "210"), null, _slot("Chemistry", "Sr MPC-A", "301"), _slot("Chem Lab", "Jr BiPC-A", "Lab-2"), null, _slot("Chemistry", "Jr MPC-A", "205")],
    Tue: [_slot("Chemistry", "Sr MPC-A", "301"), _slot("Chemistry", "Jr BiPC-A", "210"), null, _slot("Chemistry", "Jr MPC-A", "205"), _slot("Chemistry", "Sr MPC-B", "302"), null],
    Wed: [null, _slot("Chemistry", "Jr MPC-A", "205"), _slot("Chem Lab", "Sr MPC-A", "Lab-2"), _slot("Chemistry", "Jr BiPC-A", "210"), null, _slot("Mentoring", "Jr MPC-A", "205")],
    Thu: [_slot("Chemistry", "Sr MPC-A", "301"), _slot("Chemistry", "Sr MPC-B", "302"), null, null, _slot("Chemistry", "Jr BiPC-A", "210"), _slot("Chemistry", "Jr MPC-A", "205")],
    Fri: [_slot("Chemistry", "Jr MPC-A", "205"), null, _slot("Chemistry", "Sr MPC-A", "301"), _slot("Chem Lab", "Jr MPC-A", "Lab-2"), _slot("Chemistry", "Sr MPC-B", "302"), null],
    Sat: [_slot("Chemistry", "Jr BiPC-A", "210"), _slot("Chemistry", "Sr MPC-A", "301"), null, _slot("Weekly Test", "Jr BiPC", "Hall-2"), null, null],
  },
  "RZ-T-0211": {
    Mon: [_slot("Maths", "Sr MPC-A", "301"), _slot("Maths", "Sr MPC-B", "302"), _slot("Maths", "Jr MPC-A", "205"), null, _slot("HOD Meeting", "Staff", "Conf"), null],
    Tue: [_slot("Maths", "Jr MPC-A", "205"), _slot("Maths", "Jr MPC-B", "206"), null, _slot("Maths", "Sr MPC-A", "301"), _slot("Maths", "Sr MPC-B", "302"), null],
    Wed: [_slot("Maths", "Sr MPC-A", "301"), null, _slot("Maths", "Jr MPC-A", "205"), _slot("Maths", "Jr MPC-B", "206"), null, _slot("Mentoring", "Sr MPC-A", "301")],
    Thu: [_slot("Maths", "Sr MPC-B", "302"), _slot("Maths", "Sr MPC-A", "301"), _slot("Maths", "Jr MPC-A", "205"), null, _slot("Maths", "Jr MPC-B", "206"), null],
    Fri: [null, _slot("Maths", "Sr MPC-A", "301"), _slot("Maths", "Sr MPC-B", "302"), _slot("Maths", "Jr MPC-A", "205"), null, _slot("Doubt Clearing", "Sr MPC", "301")],
    Sat: [_slot("Maths", "Jr MPC-A", "205"), _slot("Maths", "Sr MPC-A", "301"), null, _slot("Weekly Test", "Sr MPC", "Hall-1"), null, null],
  },
};

// Aggregate workload figures per teaching lecturer (periods/week).
export const LECTURER_WORKLOAD = EMPLOYEES.filter((e) => e.type === "Teaching" && e.status !== "Resigned").map((e, i) => {
  const periods = 24 + ((i * 5) % 12);
  return {
    id: e.id,
    name: e.name,
    dept: e.dept,
    designation: e.designation,
    subjects: e.dept === "Physics" ? "Physics" : e.dept === "Chemistry" ? "Chemistry" : e.dept === "Mathematics" ? "Maths" : e.dept === "Biology" ? "Botany / Zoology" : "—",
    classes: 3 + (i % 3),
    periods,
    load: periods >= 32 ? "High" : periods >= 26 ? "Optimal" : "Light",
  };
});

/* ─── Dashboard activity feed ────────────────────────────────────── */
export const ACTIVITIES = [
  { text: "Ramesh Babu applied for 2 days casual leave", time: "10 min ago", icon: "fas fa-plane-departure", tone: "amber" },
  { text: "Payroll for June 2026 processed for 11 employees", time: "3 hrs ago", icon: "fas fa-wallet", tone: "gold" },
  { text: "Divya Prasanna onboarding — document verification pending", time: "5 hrs ago", icon: "fas fa-user-plus", tone: "info" },
  { text: "Appraisal cycle Apr–Sep 2026 opened for Physics", time: "Yesterday", icon: "fas fa-award", tone: "teal" },
  { text: "Exit clearance started for Naveen Chowdary", time: "2 days ago", icon: "fas fa-sign-out-alt", tone: "coral" },
];

/* ===================================================================
   STATUS STYLES
=================================================================== */
export const STATUS_STYLE = {
  Active: { bg: T.tealSoft, fg: T.teal }, Present: { bg: T.tealSoft, fg: T.teal },
  Verified: { bg: T.tealSoft, fg: T.teal }, Approved: { bg: T.tealSoft, fg: T.teal },
  Paid: { bg: T.tealSoft, fg: T.teal }, Completed: { bg: T.tealSoft, fg: T.teal },
  Cleared: { bg: T.tealSoft, fg: T.teal }, Open: { bg: T.tealSoft, fg: T.teal },
  "On Leave": { bg: T.amberSoft, fg: T.amber }, Late: { bg: T.amberSoft, fg: T.amber },
  Pending: { bg: T.amberSoft, fg: T.amber }, Probation: { bg: T.goldSoft, fg: T.gold },
  Processing: { bg: T.amberSoft, fg: T.amber }, "In Progress": { bg: T.amberSoft, fg: T.amber },
  Screening: { bg: T.infoSoft, fg: T.info }, Interview: { bg: T.infoSoft, fg: T.info },
  Offer: { bg: T.goldSoft, fg: T.gold },
  Inactive: { bg: T.coralSoft, fg: T.coral }, Absent: { bg: T.coralSoft, fg: T.coral },
  Rejected: { bg: T.coralSoft, fg: T.coral }, Resigned: { bg: T.coralSoft, fg: T.coral },
  Optimal: { bg: T.tealSoft, fg: T.teal }, High: { bg: T.coralSoft, fg: T.coral },
  Light: { bg: T.infoSoft, fg: T.info },
};

/* ===================================================================
   UI PRIMITIVES
=================================================================== */
export function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg: T.rule, fg: T.slate };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: s.bg, color: s.fg, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: s.fg }} />
      {status}
    </span>
  );
}

export function Avatar({ name, size = 36 }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");
  const hues = [T.ink, T.gold, T.teal, T.info, "#5B4B8A"];
  const hue = hues[(name || "").length % hues.length];
  return (
    <div style={{ width: size, height: size, background: hue, color: "#fff", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: size * 0.4, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

export function Card({ children, style }) {
  return (
    <div style={{ background: T.panel, border: `1px solid ${T.rule}`, borderRadius: 14, ...style }}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, sub, accent, icon }) {
  return (
    <Card style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 12, color: T.slate, marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: T.ink, lineHeight: 1 }}>{value}</div>
          {sub && <div style={{ fontSize: 12, marginTop: 6, color: accent || T.slate }}>{sub}</div>}
        </div>
        {icon && (
          <div style={{ width: 38, height: 38, borderRadius: 10, background: (accent || T.gold) + "22", color: accent || T.gold, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className={icon} />
          </div>
        )}
      </div>
    </Card>
  );
}

export function SectionHeader({ eyebrow, title, sub, action }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
      <div>
        {eyebrow && <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: T.gold, marginBottom: 4 }}>{eyebrow}</div>}
        <h2 style={{ fontSize: 22, fontWeight: 700, color: T.ink, margin: 0 }}>{title}</h2>
        {sub && <p style={{ fontSize: 13, color: T.slate, margin: "4px 0 0" }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export function Btn({ children, variant = "secondary", onClick, icon, size = "md" }) {
  const styles = {
    primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` },
    gold: { background: T.gold, color: "#fff", border: `1px solid ${T.gold}` },
    secondary: { background: T.paper, color: T.ink, border: `1px solid ${T.rule}` },
    danger: { background: T.coralSoft, color: T.coral, border: `1px solid ${T.coralSoft}` },
  };
  const pad = size === "sm" ? "6px 12px" : "9px 16px";
  return (
    <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: pad, borderRadius: 9, fontSize: size === "sm" ? 12 : 13, fontWeight: 600, cursor: "pointer", ...styles[variant] }}>
      {icon && <i className={icon} />}
      {children}
    </button>
  );
}

export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1050, background: "rgba(15,20,32,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 16, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width, marginTop: 48, background: T.panel, border: `1px solid ${T.rule}`, borderRadius: 16, boxShadow: "0 12px 48px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 22px", borderBottom: `1px solid ${T.rule}` }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: T.ink, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ border: "none", background: T.paper, borderRadius: 8, width: 30, height: 30, cursor: "pointer", color: T.ink }}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div style={{ padding: 22, maxHeight: "70vh", overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.slate, marginBottom: 6 }}>{label}</span>
      {children}
    </label>
  );
}

export const inputStyle = { width: "100%", padding: "9px 12px", borderRadius: 9, fontSize: 13, outline: "none", background: T.paper, border: `1px solid ${T.rule}`, color: T.ink };
export function Input(props) { return <input {...props} style={{ ...inputStyle, ...props.style }} />; }
export function Select(props) { return <select {...props} style={{ ...inputStyle, ...props.style }}>{props.children}</select>; }

export function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [toast, onDone]);
  if (!toast) return null;
  const isErr = toast.type === "error";
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1060, display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 12, fontSize: 13, fontWeight: 600, color: "#fff", background: isErr ? T.coral : T.ink, boxShadow: "0 8px 28px rgba(0,0,0,0.25)" }}>
      <i className={isErr ? "fas fa-times-circle" : "fas fa-check-circle"} style={{ color: isErr ? "#fff" : T.gold }} />
      {toast.msg}
    </div>
  );
}

export function EmptyRow({ colSpan, text = "No records match these filters." }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ textAlign: "center", padding: "48px 16px", color: T.slate, fontSize: 13 }}>{text}</td>
    </tr>
  );
}

/* ===================================================================
   REUSABLE DATA TABLE  (search + filters + sort + pagination)
=================================================================== */
export function DataTable({ columns, data, searchKeys = [], filters = [], pageSize = 8, onRowClick, rowActions }) {
  const [query, setQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let rows = data;
    if (query) {
      const q = query.toLowerCase();
      rows = rows.filter((r) => searchKeys.some((k) => String(r[k] || "").toLowerCase().includes(q)));
    }
    Object.entries(activeFilters).forEach(([key, val]) => {
      if (val && val !== "All") rows = rows.filter((r) => r[key] === val);
    });
    if (sortKey) {
      rows = [...rows].sort((a, b) => (a[sortKey] > b[sortKey] ? 1 : a[sortKey] < b[sortKey] ? -1 : 0) * sortDir);
    }
    return rows;
  }, [data, query, activeFilters, sortKey, sortDir, searchKeys]);

  useEffect(() => { setPage(1); }, [query, activeFilters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  const colCount = columns.length + (rowActions ? 1 : 0);

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 14 }}>
        {searchKeys.length > 0 && (
          <div style={{ position: "relative", flex: 1, minWidth: 200, maxWidth: 340 }}>
            <i className="fas fa-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.slate, fontSize: 13 }} />
            <Input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ paddingLeft: 32 }} />
          </div>
        )}
        {filters.map((f) => (
          <Select key={f.key} value={activeFilters[f.key] || "All"} onChange={(e) => setActiveFilters((s) => ({ ...s, [f.key]: e.target.value }))} style={{ width: "auto", padding: "8px 12px" }}>
            <option>All</option>
            {f.options.map((o) => <option key={o}>{o}</option>)}
          </Select>
        ))}
        <span style={{ fontSize: 12, color: T.slate, marginLeft: "auto" }}>{filtered.length} results</span>
      </div>

      <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${T.rule}` }}>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: T.paper }}>
              {columns.map((c) => (
                <th key={c.key} onClick={() => { if (c.sortable !== false) { setSortKey(c.key); setSortDir(sortKey === c.key ? -sortDir : 1); } }}
                  style={{ textAlign: "left", padding: "11px 16px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: T.slate, cursor: c.sortable === false ? "default" : "pointer", whiteSpace: "nowrap" }}>
                  {c.label}{c.sortable !== false && <i className="fas fa-sort" style={{ marginLeft: 6, opacity: 0.5, fontSize: 10 }} />}
                </th>
              ))}
              {rowActions && <th style={{ padding: "11px 16px" }} />}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && <EmptyRow colSpan={colCount} />}
            {pageRows.map((row, i) => (
              <tr key={i} onClick={() => onRowClick && onRowClick(row)}
                style={{ borderTop: `1px solid ${T.rule}`, cursor: onRowClick ? "pointer" : "default" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = T.paper)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                {columns.map((c) => (
                  <td key={c.key} style={{ padding: "12px 16px", color: T.ink }}>{c.render ? c.render(row) : row[c.key]}</td>
                ))}
                {rowActions && <td style={{ padding: "12px 16px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>{rowActions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, fontSize: 13, color: T.slate }}>
          <span>Page {page} of {totalPages}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.rule}`, background: T.panel, cursor: "pointer", opacity: page === 1 ? 0.4 : 1 }}><i className="fas fa-chevron-left" /></button>
            <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.rule}`, background: T.panel, cursor: "pointer", opacity: page === totalPages ? 0.4 : 1 }}><i className="fas fa-chevron-right" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Small labelled key/value used in profile drawers */
export function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: T.slate, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.ink }}>{value}</div>
    </div>
  );
}
